import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Category } from '@ricettario/shared';
import { api, ApiError } from '../api/client';
import { useRecipe } from '../hooks/useRecipes';
import { scaleQuantity, formatQuantity } from '../lib/scale';
import RecipeCover from '../components/RecipeCover';

export default function RecipeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: recipe, loading, error } = useRecipe(id);
  const [servings, setServings] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  if (loading) {
    return (
      <main className="max-w-xl mx-auto px-5 pt-4 space-y-4">
        <div className="skeleton h-[280px] rounded-3xl" />
        <div className="skeleton h-9 w-3/4" />
        <div className="skeleton h-4 w-1/2" />
      </main>
    );
  }
  if (error || !recipe) {
    return (
      <main className="max-w-xl mx-auto px-5 py-20 text-center space-y-5">
        <p className="text-brand text-lg font-bold">{error ?? 'Ricetta non trovata.'}</p>
        <Link to="/" className="btn-soft">← torna</Link>
      </main>
    );
  }

  const currentServings = servings ?? recipe.baseServings ?? 2;

  async function onDelete() {
    if (!recipe) return;
    if (!confirm(`Eliminare "${recipe.name}"? È definitivo.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/api/v1/recipes/${recipe.id}`);
      navigate('/', { replace: true });
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'errore');
      setDeleting(false);
    }
  }

  return (
    <main className="pb-28">
      <div className="max-w-xl mx-auto px-5 pt-5 pb-2 flex items-center justify-between">
        <Link to="/" className="btn-ghost" aria-label="torna">
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </Link>
        <div className="flex gap-1">
          <Link to={`/recipe/${recipe.id}/edit`} className="btn-ghost">modifica</Link>
          <button onClick={onDelete} className="btn-ghost text-brand" disabled={deleting}>
            {deleting ? '…' : 'elimina'}
          </button>
        </div>
      </div>

      <section className="max-w-xl mx-auto px-5 mt-3 animate-riseIn">
        <div className="card overflow-hidden">
          <div className="aspect-[4/3] relative">
            <RecipeCover recipe={recipe} size="hero" />
          </div>
          <div className="p-5">
            <h1 className="text-[28px] font-extrabold tracking-tightest leading-[1.1] mb-2">
              {recipe.name}
            </h1>
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted font-medium">
              {recipe.prepTime ? (
                <span className="flex items-center gap-1">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 2" strokeLinecap="round" />
                  </svg>
                  {recipe.prepTime} min
                </span>
              ) : null}
              <span>·</span>
              <span>base {recipe.baseServings} persone</span>
              {recipe.category && <CategoryChip category={recipe.category} />}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-xl mx-auto px-5 mt-6 animate-riseIn" style={{ animationDelay: '60ms' }}>
        <div className="flex items-end justify-between mb-4">
          <h2 className="text-xl font-extrabold tracking-tightest">Ingredienti</h2>
          <ServingsControl
            value={currentServings}
            onChange={setServings}
            base={recipe.baseServings}
          />
        </div>
        <ul className="card divide-y divide-line">
          {recipe.ingredients.map((ing) => {
            const q = scaleQuantity(ing.quantity, currentServings, recipe.baseServings);
            return (
              <li key={ing.id ?? ing.name} className="flex items-center justify-between px-5 py-3.5">
                <span className="font-medium">{ing.name}</span>
                <span className="text-muted font-mono text-sm tabular-nums">
                  {formatQuantity(q)}
                  {ing.unit ? ` ${ing.unit}` : ''}
                </span>
              </li>
            );
          })}
        </ul>
      </section>

      {recipe.steps.length > 0 && (
        <section className="max-w-xl mx-auto px-5 mt-6 animate-riseIn" style={{ animationDelay: '120ms' }}>
          <h2 className="text-xl font-extrabold tracking-tightest mb-4">Preparazione</h2>
          <ol className="space-y-3">
            {recipe.steps.map((s, idx) => (
              <li key={s.id ?? idx} className="card p-5 flex gap-4">
                <span className="shrink-0 h-10 w-10 rounded-full bg-brand text-white font-bold flex items-center justify-center">
                  {idx + 1}
                </span>
                <p className="leading-relaxed text-[15.5px] text-ink self-center">{s.content}</p>
              </li>
            ))}
          </ol>
        </section>
      )}

      {recipe.notes && (
        <section className="max-w-xl mx-auto px-5 mt-6 animate-riseIn" style={{ animationDelay: '180ms' }}>
          <h2 className="text-xl font-extrabold tracking-tightest mb-3">Appunti</h2>
          <div className="card p-5 bg-sunSoft">
            <p className="text-inkSoft leading-relaxed">{recipe.notes}</p>
          </div>
        </section>
      )}

      <div className="bottom-bar">
        <div className="max-w-xl mx-auto px-5 flex gap-3">
          {recipe.steps.length > 0 && (
            <Link
              to={`/recipe/${recipe.id}/cook`}
              state={{ servings: currentServings }}
              className="btn-brand flex-1"
            >
              Cucina
            </Link>
          )}
          <Link
            to={`/recipe/${recipe.id}/send`}
            state={{ servings: currentServings }}
            className={`btn-soft ${recipe.steps.length === 0 ? 'flex-1' : 'flex-1'}`}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 6h15l-1.5 9h-12z" />
              <circle cx="9" cy="20" r="1.5" />
              <circle cx="18" cy="20" r="1.5" />
              <path d="M6 6L5 3H2" />
            </svg>
            Spesa
          </Link>
        </div>
      </div>
    </main>
  );
}

function ServingsControl({
  value,
  onChange,
  base,
}: {
  value: number;
  onChange: (n: number | null) => void;
  base: number;
}) {
  return (
    <div className="card flex items-center gap-1 p-1">
      <button
        onClick={() => onChange(Math.max(1, value - 1))}
        disabled={value <= 1}
        className="h-9 w-9 rounded-full flex items-center justify-center disabled:opacity-30 active:bg-surface"
        aria-label="diminuisci"
      >
        −
      </button>
      <button
        onClick={() => onChange(null)}
        className="min-w-[40px] text-center text-sm font-bold"
        title={value !== base ? `torna a ${base}` : ''}
      >
        <span>{value}</span>
        <span className="text-muted text-[10px] font-medium ml-1">
          {value === 1 ? 'pers.' : 'pers.'}
        </span>
      </button>
      <button
        onClick={() => onChange(Math.min(50, value + 1))}
        disabled={value >= 50}
        className="h-9 w-9 rounded-full flex items-center justify-center disabled:opacity-30 active:bg-surface"
        aria-label="aumenta"
      >
        +
      </button>
    </div>
  );
}

const CATEGORY_COLOR: Record<Category, string> = {
  ANTIPASTO: 'clay',
  PRIMO: 'brand',
  SECONDO: 'sky',
  CONTORNO: 'sage',
  'PIATTO UNICO': 'sun',
  DOLCE: 'lavender',
};
function labelFor(cat: Category): string {
  return cat
    .toLowerCase()
    .split(' ')
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');
}
function CategoryChip({ category }: { category: Category }) {
  return (
    <span className={`chip chip-${CATEGORY_COLOR[category]}`}>{labelFor(category)}</span>
  );
}
